import React, { useState, useEffect, useRef } from 'react';
import { X, Camera, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { errorRingBuffer } from '../../utils/errorRingBuffer';
import { summarizeRecentErrors } from '../../utils/errorHumanizer';
import { apiCall } from '../../services/api.service';
import { toastSuccess, toastError } from '../../features/clu/utils/notify';

interface BugReportModalProps {
  open: boolean;
  onClose: () => void;
}

export default function BugReportModal({ open, onClose }: BugReportModalProps) {
  const [description, setDescription] = useState('');
  const [happenedOnScreen, setHappenedOnScreen] = useState<boolean | null>(null);
  const [wasRecent, setWasRecent] = useState<boolean | null>(null);
  const [screenshotDataUrl, setScreenshotDataUrl] = useState<string | null>(null);
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const captureTriggered = useRef(false);

  // Screenshot capture is best-effort: html2canvas can hang for a long time on
  // heavy pages, so it races an 8s timeout and NEVER blocks report submission.
  const SCREENSHOT_TIMEOUT_MS = 8000;

  useEffect(() => {
    if (!open) {
      captureTriggered.current = false;
      return;
    }
    if (captureTriggered.current) return;
    captureTriggered.current = true;

    setCapturingScreenshot(true);
    const portalEl = document.getElementById('headlessui-portal-root') as HTMLElement | null;
    if (portalEl) portalEl.style.visibility = 'hidden';
    const inputs = Array.from(
      document.querySelectorAll('input:not([type="checkbox"]):not([type="radio"]):not([type="hidden"])')
    ) as HTMLInputElement[];
    const savedValues = inputs.map((i) => i.value);
    inputs.forEach((i) => { i.value = ''; });

    let settled = false;
    const restore = () => {
      inputs.forEach((i, idx) => { i.value = savedValues[idx]; });
      if (portalEl) portalEl.style.visibility = '';
    };
    const finish = (dataUrl: string | null, blob: Blob | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      restore();
      setScreenshotDataUrl(dataUrl);
      setScreenshotBlob(blob);
      setCapturingScreenshot(false);
    };
    const timeoutId = window.setTimeout(() => finish(null, null), SCREENSHOT_TIMEOUT_MS);

    import('html2canvas').then(({ default: html2canvas }) => {
      return html2canvas(document.body, {
        useCORS: true,
        scale: 0.5,
        ignoreElements: (el) => el.hasAttribute('data-bug-report-mask'),
      });
    }).then((canvas) => {
      const dataUrl = canvas.toDataURL('image/png');
      return new Promise<{ dataUrl: string; blob: Blob | null }>((resolve) =>
        canvas.toBlob((b) => resolve({ dataUrl, blob: b }), 'image/png')
      );
    }).then(({ dataUrl, blob }) => {
      finish(dataUrl, blob);
    }).catch(() => {
      finish(null, null);
    });
  }, [open]);

  function handleClose() {
    if (submitting) return;
    setDescription('');
    setHappenedOnScreen(null);
    setWasRecent(null);
    setScreenshotDataUrl(null);
    setScreenshotBlob(null);
    setCapturingScreenshot(false);
    captureTriggered.current = false;
    onClose();
  }

  async function handleSubmit() {
    if (submitting) return;
    setSubmitting(true);

    let sentryEventId: string | null = null;
    try {
      const Sentry = await import('@sentry/react');
      sentryEventId = Sentry.lastEventId() ?? null;
    } catch {
      sentryEventId = null;
    }

    const agentId: number | null = (() => {
      try {
        const raw = localStorage.getItem('clu-selected-agent');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed?.id ?? parsed ?? null;
      } catch {
        return null;
      }
    })();

    const frontendLogs = errorRingBuffer.snapshot();

    const payload = {
      route: window.location.pathname,
      description: description.trim() || null,
      happened_on_this_screen: happenedOnScreen,
      was_recent: wasRecent,
      sentry_event_id: sentryEventId,
      agent_id: agentId,
      frontend_logs: frontendLogs,
      extra_data: {
        user_agent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        language: navigator.language,
        url: window.location.href,
        error_summaries: summarizeRecentErrors(frontendLogs).map((e) => e.summary),
      },
    };

    const formData = new FormData();
    formData.append('payload', JSON.stringify(payload));
    if (screenshotBlob) {
      formData.append('screenshot', screenshotBlob, 'screenshot.png');
    }

    try {
      await apiCall('bug-reports', 'POST', formData);
      errorRingBuffer.clear();
      toastSuccess('Bug report submitted', 'Thank you — we will look into it.');
      handleClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Could not submit report';
      toastError('Submission failed', message);
    } finally {
      setSubmitting(false);
    }
  }

  const bufferSnapshot = errorRingBuffer.snapshot();
  const bufferCount = bufferSnapshot.length;
  const errorSummaries = summarizeRecentErrors(bufferSnapshot);

  return (
    <Dialog open={open} onClose={handleClose} size="md">
      <DialogHeader>
        <DialogTitle>Report a bug</DialogTitle>
        <button
          type="button"
          onClick={handleClose}
          disabled={submitting}
          className="rounded-md p-1 text-ink-muted hover:text-ink hover:bg-canvas transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </DialogHeader>

      <DialogContent className="space-y-5">
        <div className="flex gap-3 rounded-xl border border-line bg-canvas p-3">
          {capturingScreenshot ? (
            <div className="flex h-20 w-28 shrink-0 items-center justify-center rounded-lg bg-surface">
              <Camera className="h-6 w-6 animate-pulse text-ink-muted" />
            </div>
          ) : screenshotDataUrl ? (
            <img
              src={screenshotDataUrl}
              alt="Screenshot preview"
              className="h-20 w-28 shrink-0 rounded-lg object-cover border border-line"
            />
          ) : (
            <div className="flex h-20 w-28 shrink-0 items-center justify-center rounded-lg bg-surface border border-line">
              <AlertCircle className="h-6 w-6 text-ink-muted" />
            </div>
          )}
          <div className="flex flex-col justify-center gap-1">
            <p className="text-sm font-medium text-ink">
              {capturingScreenshot
                ? 'Capturing screenshot…'
                : screenshotDataUrl
                ? 'Screenshot captured'
                : 'Screenshot unavailable'}
            </p>
            <p className="text-xs text-ink-muted">
              Route: <span className="font-mono">{window.location.pathname}</span>
            </p>
            {bufferCount > 0 && (
              <div className="space-y-0.5">
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  {bufferCount} frontend log{bufferCount !== 1 ? 's' : ''} captured
                </p>
                {errorSummaries.map((err) => (
                  <p
                    key={err.summary}
                    className="text-xs text-ink-muted truncate max-w-[260px]"
                    title={err.detail}
                  >
                    • {err.summary}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>

        <fieldset>
          <legend className="text-sm font-medium text-ink mb-2">
            Did this happen on the current screen?
          </legend>
          <div className="flex gap-3">
            {([true, false] as const).map((val) => (
              <button
                key={String(val)}
                type="button"
                onClick={() => setHappenedOnScreen(val)}
                className={clsx(
                  'flex-1 h-10 rounded-md border text-sm font-medium transition-colors',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500',
                  happenedOnScreen === val
                    ? 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                    : 'border-line bg-surface text-ink-muted hover:bg-canvas'
                )}
              >
                {val ? 'Yes' : 'No'}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="text-sm font-medium text-ink mb-2">
            Did it happen in the last few minutes?
          </legend>
          <div className="flex gap-3">
            {([true, false] as const).map((val) => (
              <button
                key={String(val)}
                type="button"
                onClick={() => setWasRecent(val)}
                className={clsx(
                  'flex-1 h-10 rounded-md border text-sm font-medium transition-colors',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500',
                  wasRecent === val
                    ? 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                    : 'border-line bg-surface text-ink-muted hover:bg-canvas'
                )}
              >
                {val ? 'Yes' : 'No'}
              </button>
            ))}
          </div>
        </fieldset>

        <div>
          <label
            htmlFor="bug-description"
            className="block text-sm font-medium text-ink mb-1.5"
          >
            Describe what happened{' '}
            <span className="text-ink-muted font-normal">(optional)</span>
          </label>
          <textarea
            id="bug-description"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Steps to reproduce, what you expected, what happened instead…"
            className={clsx(
              'w-full resize-none rounded-xl border border-line bg-canvas px-3 py-2.5',
              'text-sm text-ink placeholder:text-ink-muted',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
              'transition-colors'
            )}
          />
        </div>
      </DialogContent>

      <DialogFooter>
        <button
          type="button"
          onClick={handleClose}
          disabled={submitting}
          className={clsx(
            'h-10 px-4 rounded-md border border-line',
            'text-sm font-medium text-ink-muted hover:text-ink hover:bg-canvas',
            'transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className={clsx(
            'h-10 px-4 rounded-md',
            'text-sm font-medium text-white',
            'bg-blue-600 hover:bg-blue-700',
            'transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          {submitting ? 'Submitting…' : 'Submit report'}
        </button>
      </DialogFooter>
    </Dialog>
  );
}
